import { privateApi } from '@/config/axios.config';
import { isAxiosError } from 'axios';

// Usar la instancia privada de axios que ya tiene interceptores configurados
const propertiesApi = privateApi;

export interface Property {
  id: string;
  title: string;
  description: string;
  type?: 'house' | 'apartment' | 'land' | 'commercial';
  propertyType?: 'house' | 'apartment' | 'land' | 'commercial';
  status: 'active' | 'sold' | 'rented' | 'inactive';
  price: number;
  currency: string;
  bedrooms?: number;
  bathrooms?: number;
  size?: number;
  // GH#2409 — lo que el API trae PLANO y el saga tiraba (ver utils/propiedad-edicion.ts). Todo opcional: ausente = no cargado.
  parkingSpaces?: number;
  floors?: number;
  yearBuilt?: number;
  condition?: string;
  isFeatured?: boolean;
  metadata?: { notes?: string; [clave: string]: unknown };
  location: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
    neighborhood?: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  features: {
    bedrooms?: number;
    bathrooms?: number;
    area: number;
    parking?: number;
    floor?: number;
    totalFloors?: number;
    yearBuilt?: number;
    condition?: 'excellent' | 'good' | 'fair' | 'needs_repair';
  };
  images: string[];
  agentId: string;
  agentName: string;
  listingDate: Date;
  lastUpdated: Date;
  views: number;
  inquiries: number;
  tags: string[];
  amenities: string[];
  isPublished?: boolean;
  publishedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface PropertyCreateDTO {
  title: string;
  description: string;
  type?: string;
  propertyType?: string;
  price: number;
  currency: string;
  location: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  features: {
    bedrooms?: number;
    bathrooms?: number;
    area: number;
    parking?: number;
    floor?: number;
    totalFloors?: number;
    yearBuilt?: number;
    condition?: string;
  };
  agentId: string;
  tags?: string[];
  amenities?: string[];
  images?: File[];
}

export interface PropertyUpdateDTO extends Partial<PropertyCreateDTO> {
  id: string;
  status?: string;
  description?: string;
}

export interface PropertyFilters {
  type?: string;
  propertyType?: string;
  status?: string;
  minPrice?: number;
  maxPrice?: number;
  city?: string;
  state?: string;
  bedrooms?: number;
  bathrooms?: number;
  minArea?: number;
  maxArea?: number;
  agentId?: string;
  tags?: string[];
  search?: string;
  page?: number;
  limit?: number;
}

export interface PropertyStats {
  totalProperties: number;
  activeProperties: number;
  soldProperties: number;
  rentedProperties: number;
  inactiveProperties: number;
  averagePrice: number;
  totalValue: number;
  propertiesByType: Record<string, number>;
  propertiesByStatus: Record<string, number>;
  topPerformingAgent: string;
  monthlyGrowth: number;
}

export interface PropertyComparisonLocation {
  lat?: number;
  lng?: number;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  neighborhood?: string;
}

export interface PropertyComparisonAgent {
  id: string;
  fullName: string;
  email: string;
}

export interface PropertyComparisonMedia {
  id?: string;
  url: string;
  thumbnailUrl?: string;
  isPrimary?: boolean;
  alt?: string;
}

export interface PropertyComparisonItem {
  id: string;
  slug: string;
  title: string;
  propertyType?: string;
  status: string;
  price: number;
  currency: string;
  size?: number;
  landSize?: number;
  bedrooms?: number;
  bathrooms?: number;
  parkingSpaces?: number;
  amenities: string[];
  features: string[];
  isFeatured: boolean;
  isPublished: boolean;
  publishedAt?: string;
  location: PropertyComparisonLocation;
  agent?: PropertyComparisonAgent;
  images?: PropertyComparisonMedia[];
}

export interface PropertyComparisonResult {
  items: PropertyComparisonItem[];
  missingIds: string[];
}

export const propertiesApiService = {
  // Obtener todas las propiedades con filtros
  async getProperties(filters: PropertyFilters = {}): Promise<{
    properties: Property[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.type && filters.type !== 'all')
      params.append('type', filters.type);
    if (filters.propertyType && filters.propertyType !== 'all')
      params.append('propertyType', filters.propertyType);
    if (filters.status && filters.status !== 'all')
      params.append('status', filters.status);
    if (filters.minPrice)
      params.append('minPrice', filters.minPrice.toString());
    if (filters.maxPrice)
      params.append('maxPrice', filters.maxPrice.toString());
    if (filters.city) params.append('city', filters.city);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await propertiesApi.get(
      `/properties?${params.toString()}`
    );

    // Adaptar la respuesta del backend al formato esperado
    const backendData = response.data.data || response.data;
    const properties = backendData.properties || backendData || [];
    const total = backendData.total || properties.length;

    return {
      properties: properties,
      total: total,
      page: backendData.page || 1,
      limit: backendData.limit || filters.limit || 10,
      totalPages: backendData.totalPages || 1,
    };
  },

  // Obtener una propiedad por ID
  async getPropertyById(id: string): Promise<Property> {
    const response = await propertiesApi.get(`/properties/${id}`);
    // El backend puede devolver data.data o directamente data
    return response.data.data || response.data;
  },

  // Crear nueva propiedad
  async createProperty(propertyData: any): Promise<Property> {
    // El backend espera JSON, no FormData
    const response = await propertiesApi.post('/properties', propertyData);
    return response.data;
  },

  // Actualizar propiedad
  async updateProperty(
    id: string,
    propertyData: PropertyUpdateDTO,
  ): Promise<Property> {
    const response = await propertiesApi.patch(`/properties/${id}`, propertyData);
    return response.data?.data || response.data;
  },

  // Eliminar propiedad
  async deleteProperty(id: string): Promise<void> {
    await propertiesApi.delete(`/properties/${id}`);
  },

  // Obtener estadísticas de propiedades
  async getPropertyStats(): Promise<PropertyStats> {
    // Por ahora, calcular estadísticas básicas desde las propiedades
    const propertiesResponse = await this.getProperties({ limit: 1000 });
    const properties = propertiesResponse.properties;

    const totalProperties = properties.length;
    const activeProperties = properties.filter(
      p => p.status === 'active'
    ).length;
    const soldProperties = properties.filter(p => p.status === 'sold').length;
    const rentedProperties = properties.filter(
      p => p.status === 'rented'
    ).length;
    const inactiveProperties = properties.filter(
      p => p.status === 'inactive'
    ).length;

    const totalValue = properties.reduce((sum, p) => sum + (p.price || 0), 0);
    const averagePrice = totalProperties > 0 ? totalValue / totalProperties : 0;

    return {
      totalProperties,
      activeProperties,
      soldProperties,
      rentedProperties,
      inactiveProperties,
      averagePrice,
      totalValue,
      propertiesByType: {},
      propertiesByStatus: {},
      topPerformingAgent: 'N/A',
      monthlyGrowth: 0,
    };
  },

  async compareProperties(ids: string[]): Promise<PropertyComparisonResult> {
    if (!ids || ids.length === 0) {
      return { items: [], missingIds: [] };
    }

    const searchParams = new URLSearchParams();
    ids
      .map(id => id.trim())
      .filter(id => id.length > 0)
      .forEach(id => searchParams.append('ids', id));

    if ([...searchParams.keys()].length === 0) {
      return { items: [], missingIds: [] };
    }

    const response = await propertiesApi.get(`/properties/compare?${searchParams.toString()}`);
    const data = response.data?.data || response.data;

    return {
      items: data?.items || [],
      missingIds: data?.missingIds || [],
    };
  },

  // Actualizar estado de la propiedad
  async updatePropertyStatus(id: string, status: string): Promise<Property> {
    const normalizedStatus = normalizeStatusForApi(status);

    const response = await propertiesApi.patch(`/properties/${id}/status`, {
      status: normalizedStatus,
    });
    return response.data;
  },

  // Duplicar propiedad
  async duplicateProperty(id: string, newTitle?: string): Promise<Property> {
    const response = await propertiesApi.post(`/properties/${id}/duplicate`, {
      title: newTitle,
    });
    return response.data;
  },

  // Alternar visibilidad (público/privado)
  async toggleVisibility(id: string): Promise<Property> {
    const response = await propertiesApi.patch(`/properties/${id}/visibility`);
    return response.data;
  },

  async upsertPropertyStatus(
    property: Property,
    status: string
  ): Promise<Property> {
    try {
      return await this.updatePropertyStatus(property.id, status);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        const createPayload = mapPropertyToCreatePayload(property, status);

        const createdProperty = await this.createProperty(createPayload);

        if (createdProperty?.id) {
          try {
            if (
              normalizeStatusForApi(createdProperty.status || '') !==
              normalizeStatusForApi(status)
            ) {
              return await this.updatePropertyStatus(
                createdProperty.id,
                status
              );
            }
            return createdProperty;
          } catch (statusError) {
            return {
              ...createdProperty,
              status,
            };
          }
        }

        return {
          ...property,
          ...createdProperty,
          status: normalizeStatusForApi(status),
        };
      }

      throw error;
    }
  },

  // Obtener propiedades destacadas
  async getFeaturedProperties(limit: number = 10): Promise<Property[]> {
    const response = await propertiesApi.get(
      `/properties/featured?limit=${limit}`
    );
    return response.data;
  },

  // Obtener propiedades por agente
  async getPropertiesByAgent(agentId: string): Promise<Property[]> {
    const response = await propertiesApi.get(`/properties/agent/${agentId}`);
    return response.data;
  },

  // Subir imágenes de la propiedad
  async uploadPropertyImages(
    id: string,
    images: File[]
  ): Promise<{ imageUrls: string[] }> {
    const formData = new FormData();
    images.forEach(image => {
      formData.append(`images`, image);
    });

    const response = await propertiesApi.post(
      `/properties/${id}/images`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  // Eliminar imagen de la propiedad
  async deletePropertyImage(id: string, imageUrl: string): Promise<void> {
    await propertiesApi.delete(`/properties/${id}/images`, {
      data: { imageUrl },
    });
  },

  // Obtener estadísticas de vistas
  async getPropertyViews(
    id: string,
    period: '7d' | '30d' | '90d'
  ): Promise<{
    views: number;
    inquiries: number;
    viewsByDay: Array<{ date: string; views: number }>;
  }> {
    const response = await propertiesApi.get(
      `/properties/${id}/views?period=${period}`
    );
    return response.data;
  },

  // Marcar propiedad como vista
  async markPropertyAsViewed(
    id: string,
    viewerData: {
      ip: string;
      userAgent: string;
      referrer?: string;
    }
  ): Promise<void> {
    await propertiesApi.post(`/properties/${id}/view`, viewerData);
  },

  // Obtener propiedades similares
  async getSimilarProperties(
    id: string,
    limit: number = 5
  ): Promise<Property[]> {
    const response = await propertiesApi.get(
      `/properties/${id}/similar?limit=${limit}`
    );
    return response.data;
  },

  // Exportar propiedades
  async exportProperties(
    filters: PropertyFilters,
    format: 'csv' | 'excel'
  ): Promise<Blob> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value.toString());
      }
    });
    params.append('format', format);

    const response = await propertiesApi.get(
      `/properties/export?${params.toString()}`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  },
};

export default propertiesApiService;

const mapPropertyToCreatePayload = (property: Property, status: string) => {
  const location = property.location || {
    address: 'Dirección no especificada',
    city: 'Ciudad no especificada',
    state: 'Provincia no especificada',
    zipCode: '00000',
  };

  const features = property.features || {
    bedrooms: property.bedrooms ?? 0,
    bathrooms: property.bathrooms ?? 0,
    area: property.size ?? 0,
    parking: 0,
    floor: undefined,
    totalFloors: undefined,
    yearBuilt: undefined,
    condition: undefined,
  };

  return {
    title: property.title || 'Propiedad sin título',
    description: property.description || '',
    type: property.type || property.propertyType || 'apartment',
    propertyType: property.propertyType || property.type || 'apartment',
    price: property.price ?? 0,
    currency: property.currency || 'USD',
    location: {
      address: location.address || 'Dirección no especificada',
      city: location.city || 'Ciudad no especificada',
      state: location.state || 'Provincia no especificada',
      zipCode: location.zipCode || '00000',
      coordinates: location.coordinates,
    },
    features: {
      bedrooms: features.bedrooms ?? property.bedrooms ?? 0,
      bathrooms: features.bathrooms ?? property.bathrooms ?? 0,
      area: features.area ?? property.size ?? 0,
      parking: features.parking ?? 0,
      floor: features.floor,
      totalFloors: features.totalFloors,
      yearBuilt: features.yearBuilt,
      condition: features.condition,
    },
    agentId: property.agentId || 'unassigned',
    tags: property.tags || [],
    amenities: property.amenities || [],
    status: normalizeStatusForApi(status),
  };
};

const ALLOWED_PROPERTY_STATUSES = [
  'available',
  'sold',
  'rented',
  'negotiation',
  'temp_rental',
  'reserved',
  'off_market',
] as const;

const STATUS_ALIASES: Record<string, typeof ALLOWED_PROPERTY_STATUSES[number]> =
  {
    active: 'available',
    disponible: 'available',
    available: 'available',
    inactive: 'off_market',
    inactiva: 'off_market',
    off_market: 'off_market',
    desactivada: 'off_market',
    vendida: 'sold',
    sold: 'sold',
    alquilada: 'rented',
    rentada: 'rented',
    rented: 'rented',
    negociacion: 'negotiation',
    negotiation: 'negotiation',
    temporal: 'temp_rental',
    temp_rental: 'temp_rental',
    reservada: 'reserved',
    reserved: 'reserved',
  };

const normalizeStatusForApi = (
  status: string,
  fallback: typeof ALLOWED_PROPERTY_STATUSES[number] = 'available'
): typeof ALLOWED_PROPERTY_STATUSES[number] => {
  if (!status) {
    return fallback;
  }

  const lowerStatus = status.toLowerCase();

  if (
    (ALLOWED_PROPERTY_STATUSES as readonly string[]).includes(lowerStatus)
  ) {
    return lowerStatus as typeof ALLOWED_PROPERTY_STATUSES[number];
  }

  if (STATUS_ALIASES[lowerStatus]) {
    return STATUS_ALIASES[lowerStatus];
  }

  return fallback;
};

export const isPropertyAvailable = (status?: string) =>
  normalizeStatusForApi(status || '') === 'available';

export const getInactiveStatus = () => 'off_market';

export const getActiveStatus = () => 'available';

export const toHumanStatusLabel = (status?: string) => {
  switch (normalizeStatusForApi(status || '')) {
    case 'available':
      return 'disponible';
    case 'off_market':
      return 'fuera del mercado';
    case 'sold':
      return 'vendida';
    case 'rented':
      return 'alquilada';
    case 'reserved':
      return 'reservada';
    case 'temp_rental':
      return 'alquiler temporal';
    case 'negotiation':
      return 'en negociación';
    default:
      return 'desconocido';
  }
};
